/**
 * Matrix storage order for 4x4 transformation matrices.
 * - 'column-major': Used by Three.js, WebGL, OpenGL (columns are contiguous in memory)
 * - 'row-major': Used by DirectX, some math libraries (rows are contiguous in memory)
 */
export type MatrixOrder = 'column-major' | 'row-major';

const COLUMN_STRIDE = 4;

/** Column indices for 4x4 matrix axes */
export const MatrixColumn = {
  X: 0,
  Y: 1,
  Z: 2,
  W: 3,
} as const;

export type MatrixColumnIndex =
  (typeof MatrixColumn)[keyof typeof MatrixColumn];

/**
 * Gets the starting index for a column in a 4x4 matrix based on storage order.
 * @param columnIndex - 0 for X, 1 for Y, 2 for Z, 3 for W
 * @param order - Matrix storage order
 * @returns The starting index of the column in the elements array
 */
function getColumnStartIndex(
  columnIndex: MatrixColumnIndex,
  order: MatrixOrder
): number {
  // Column-major: column N starts at index N*4 (columns are contiguous)
  // Row-major: column N starts at index N (rows are contiguous, column elements are 4 apart)
  return order === 'column-major' ? columnIndex * COLUMN_STRIDE : columnIndex;
}

/**
 * Gets the stride between elements in the same column based on matrix order.
 * @param order - Matrix storage order
 * @returns The stride value (1 for column-major, 4 for row-major)
 */
function getColumnStride(order: MatrixOrder): number {
  // Column-major: elements in a column are contiguous (stride = 1)
  // Row-major: elements in a column are 4 apart (stride = 4)
  return order === 'column-major' ? 1 : COLUMN_STRIDE;
}

/**
 * Gets the magnitude (length) of a column vector in a 4x4 matrix.
 * Uses only the first 3 elements of the column (x, y, z components).
 * Useful for extracting scale from a transformation matrix.
 *
 * @param elements - The 16-element matrix array
 * @param columnIndex - 0 for X, 1 for Y, 2 for Z
 * @param order - Matrix storage order (default: 'column-major' for Three.js)
 * @returns The magnitude (length) of the column vector
 *
 * @example
 * ```ts
 * // Three.js Matrix4 (column-major)
 * const scaleX = getMatrixColumnMagnitude(matrix.elements, MatrixColumn.X);
 * const scaleY = getMatrixColumnMagnitude(matrix.elements, MatrixColumn.Y);
 * ```
 */
export function getMatrixColumnMagnitude(
  elements: ArrayLike<number>,
  columnIndex: MatrixColumnIndex,
  order: MatrixOrder = 'column-major'
): number {
  const start = getColumnStartIndex(columnIndex, order);
  const stride = getColumnStride(order);

  const x = elements[start];
  const y = elements[start + stride];
  const z = elements[start + stride * MatrixColumn.Z];

  return Math.sqrt(x * x + y * y + z * z);
}

/**
 * Scales a column in a 4x4 matrix by a factor.
 * Modifies only the first 3 elements of the column (x, y, z components).
 * Mutates the elements array in place.
 *
 * @param elements - The 16-element matrix array (mutated in place)
 * @param columnIndex - 0 for X, 1 for Y, 2 for Z
 * @param factor - The scale factor to apply
 * @param order - Matrix storage order (default: 'column-major' for Three.js)
 *
 * @example
 * ```ts
 * // Double the Y-axis scale in a Three.js Matrix4
 * scaleMatrixColumn(matrix.elements, MatrixColumn.Y, 2.0);
 * ```
 */
export function scaleMatrixColumn(
  elements: { [index: number]: number },
  columnIndex: MatrixColumnIndex,
  factor: number,
  order: MatrixOrder = 'column-major'
): void {
  const start = getColumnStartIndex(columnIndex, order);
  const stride = getColumnStride(order);

  elements[start] *= factor;
  elements[start + stride] *= factor;
  elements[start + stride * MatrixColumn.Z] *= factor;
}
